import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import API from "lib/api/API2";
import { Report } from "lib/models/Report";
import LicenseRestriction from "views/Components/License";
import ScheduledReportModelForm from "views/Components/Reporting/ScheduledReportModelForm";
import { getAggregation } from "views/Components/ServerTable";

const PREFIX = "SaveRequestModal";
const classes = {
  dialogContent: `${PREFIX}-dialogContent`,
  checkBoxContainer: `${PREFIX}-checkBoxContainer`,
  checkBoxText: `${PREFIX}-checkBoxText`,
  dialogTitle: `${PREFIX}-dialogTitle`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialogContent}`]: {
    paddingTop: `${theme.shape.padding}px !important`,
  },
  [`& .${classes.checkBoxContainer}`]: {
    marginLeft: -10,
    display: "flex",
    alignItems: "center",
  },
  [`& .${classes.checkBoxText}`]: {
    marginBottom: 0,
    fontWeight: "bold",
  },
  [`& .${classes.dialogTitle}`]: {
    paddingBottom: 0,
  },
}));

const SaveRequestModal = ({
  activeFields,
  controls,
  displayMessage,
  onClose,
  open,
}) => {
  const [name, setName] = useState("");
  const [scheduledReport, setScheduledReport] = useState(null);

  const savePreset = async () => {
    let response = null;
    const queryParams = [];
    const transform = [];
    for (const param of Object.values(controls)) {
      const newParam = { ...param };
      delete newParam.uuid;

      const aggParam = getAggregation(newParam);
      if (aggParam) {
        transform.push(aggParam);
      } else if (param.field !== "search") {
        queryParams.push(newParam);
      }
    }
    const payload = {
      namespace: "finding",
      queryParams: queryParams,
      resultParams: {
        outputFields: activeFields.filter((f) => f !== "ownersExpanded"),
        transform,
      },
      name,
    };
    try {
      const api = new API();
      response = await api.post("query", payload);
    } catch (err) {
      console.error(err);
      displayMessage({
        severity: "error",
        message: `Failed to save search preset: ${err}`,
        noAutoHide: true,
      });
    }
    return response;
  };

  const handleSave = async () => {
    // Search preset form validation
    if (!name || name === "") {
      displayMessage({
        severity: "error",
        message: "Search preset requires a name",
        noAutoHide: true,
      });
      return;
    }
    // Scheduled report form validation
    if (scheduledReport) {
      if (!scheduledReport.recipients.length) {
        displayMessage({
          severity: "error",
          message: "Scheduled report requires at least one recipient",
          noAutoHide: true,
        });
        return;
      }
      if (!scheduledReport.name) {
        displayMessage({
          severity: "error",
          message: "Scheduled report requires a name",
          noAutoHide: true,
        });
        return;
      }
    }

    const response = await savePreset();
    if (!response) {
      return;
    }

    // If checkbox was selected, create a scheduled report from the
    // query that we just saved. The time period from the query will
    // be overridden by the time period from the form in the report
    // service when the report is ran.
    if (scheduledReport) {
      try {
        scheduledReport.set({
          dataQueryId: response.data.id,
          dataSource: response.data.namespace,
        });
        await scheduledReport.create();
      } catch (err) {
        console.error(err);
        displayMessage({
          severity: "error",
          message: `Failed to create scheduled report: ${err}`,
          noAutoHide: true,
        });
        return;
      }
    }

    displayMessage({
      severity: "success",
      message: "Save successful",
    });

    onClose();
    setScheduledReport(null);
  };

  const handleScheduledReportCheckbox = (event) => {
    if (event.target.checked) {
      setScheduledReport(new Report());
    } else {
      setScheduledReport(null);
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      datacy={"reportSaveModal"}
      fullWidth={true}
      maxWidth={"sm"}
    >
      <DialogTitle className={classes.dialogTitle} id="form-dialog-title">
        Save Search Preset
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <TextField
          autoFocus
          size="small"
          id="name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          datacy={"reportSaveInput"}
        />
        <LicenseRestriction
          requires="report.create"
          renderDisallow={() => null}
        >
          <div className={classes.checkBoxContainer}>
            <Checkbox
              color={"primary"}
              checked={!!scheduledReport}
              onChange={handleScheduledReportCheckbox}
            />
            <p className={classes.checkBoxText}>
              Create a scheduled report for the saved preset
            </p>
          </div>
        </LicenseRestriction>
        {scheduledReport && (
          <ScheduledReportModelForm model={scheduledReport} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          datacy={"reportSaveModalBtn"}
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

SaveRequestModal.propTypes = {
  activeFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  controls: PropTypes.object.isRequired,
  displayMessage: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default SaveRequestModal;
